import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import HealthMemoryCard from "@/components/ui/atoms/HealthMemoryCard";
import { IconEdit, IconCircleX, IconPlus } from "@tabler/icons-react";
import styles from "@/pages/documents/document.module.css";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { PAGE_SIZE } from "@/utils/constants";
import { MButton } from "@/components/ui/atoms/MButtons";
import IconBubble from "@/components/icons/IconBubble";
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import useHealthMemory from "@/hooks/useHealthMemory";
import { HealthMemoryService } from "@/services/HealthMemoryService";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { HealthMemoryResponseDto } from "@/types/health-memory";

const HealthMemoryList: React.FC = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memoryToDelete, setMemoryToDelete] =
    useState<HealthMemoryResponseDto | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;

  const [page, setPage] = useState(2); // next page to fetch
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [memories, setMemories] = useState<HealthMemoryResponseDto[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Stable service reference for polling — avoids going through the hook's
  // setLoading state, which would trigger re-renders on every poll tick and
  // cause MInfiniteScroll to recreate its IntersectionObserver each time.
  const pollServiceRef = useRef<HealthMemoryService | null>(null);
  if (!pollServiceRef.current) {
    pollServiceRef.current = new HealthMemoryService(memberId);
  }

  const {
    fetchHealthMemories,
    deleteHealthMemory,
    getVoiceNoteUrl,
    patchTranscript,
    retryTranscript,
  } = useHealthMemory(memberId);

  // Preserve scroll position when navigating away to edit/add a memory and
  // back. Mirrors DocumentListing — saves scroll + paginated item count before
  // leaving, refetches enough pages on return to land back where we were.
  // The hook's `loading` arg must start true and flip false only after the
  // list rows are actually in the DOM, otherwise it would restore on the
  // first render (loader still visible) and mark itself done forever.
  const isStillLoading = !initialLoadDone || isLoading;
  const { saveScroll, saveItemCount, getSavedItemCount } = useScrollRestore(
    "healthMemoryListing",
    isStillLoading
  );

  useEffect(() => {
    if (!memberId) return;
    // (Re)load for selected member, always page 1
    (async () => {
      setIsLoading(true);
      setInitialLoadDone(false);

      setMemories([]);
      setPage(2);
      setHasMore(true);

      const savedCount = getSavedItemCount();

      const data = await fetchHealthMemories({ page: 1, pageSize: PAGE_SIZE });
      let allData: HealthMemoryResponseDto[] = data || [];
      let nextPage = 2;
      let more = allData.length === PAGE_SIZE;

      // If we're returning to the listing after an edit/add, refetch as many
      // pages as we had before so the scroll-restore lands on the right row.
      if (savedCount && savedCount > PAGE_SIZE) {
        while (more && allData.length < savedCount) {
          const moreData = await fetchHealthMemories({
            page: nextPage,
            pageSize: PAGE_SIZE,
          });
          const chunk: HealthMemoryResponseDto[] = moreData || [];
          if (chunk.length === 0) {
            more = false;
            break;
          }
          allData = [...allData, ...chunk];
          nextPage += 1;
          more = chunk.length === PAGE_SIZE;
        }
      }

      setMemories(allData);
      setPage(nextPage);
      setHasMore(more);

      setIsLoading(false);
      setInitialLoadDone(true);

      // Track page load with total memories count
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
        additionalUserProperties: {
          total_memories: String(allData.length),
        },
      });
    })();
  }, [memberId]);

  // D-58-07: poll every 5s while any card is transcribing; clear when all terminal
  const hasTranscribingCards = memories.some(
    (m) =>
      m.transcriptStatus === "pending" || m.transcriptStatus === "processing"
  );
  // Refs keep interval and fetchNextPage closures current without adding
  // primitive state to effect deps (which would reset timers or recreate
  // the onLoadMore reference passed to MInfiniteScroll).
  const memoriesLengthRef = useRef(memories.length);
  memoriesLengthRef.current = memories.length;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    if (!hasTranscribingCards) return;
    const svc = pollServiceRef.current!;
    const timer = setInterval(async () => {
      // Call the service directly — bypassing the hook's setLoading so we
      // don't trigger re-renders that recreate MInfiniteScroll's observer.
      const result = await svc.getHealthMemories({
        pagination: {
          page: 1,
          pageSize: Math.max(memoriesLengthRef.current, PAGE_SIZE),
        },
        sort: [{ field: "year", order: "desc" }],
        filters: {},
      });
      setMemories(result?.data || []);
    }, 5000);
    return () => clearInterval(timer);
  }, [hasTranscribingCards]);

  // Truly stable reference: fetchHealthMemories is the only dep that can
  // legitimately change (when memberId changes). All other mutable values
  // are read through refs, so onLoadMore never causes MInfiniteScroll to
  // recreate its IntersectionObserver during normal load cycles.
  const fetchNextPage = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) return;
    setIsLoading(true);
    const data = await fetchHealthMemories({
      page: pageRef.current,
      pageSize: PAGE_SIZE,
    });
    setMemories((prev) => [...prev, ...(data || [])]);
    setPage((prev) => prev + 1);
    setHasMore((data || []).length === PAGE_SIZE);
    setIsLoading(false);
  }, [fetchHealthMemories]);

  return (
    <div
      className="profile-page-root bg-white"
      // `.profile-page-root` adds 140px + safe-area bottom padding so content
      // doesn't get hidden behind the floating footer nav. This route already
      // reserves space for the footer via NoFooterLayout (showFooter), so the
      // extra padding becomes dead scrollable area below the last card.
      style={{ paddingBottom: `env(safe-area-inset-bottom)` }}
    >
      <FormHeader
        subHeading="Health memory"
        module="healthmemory"
        memberInfo={memberInfo}
        showProfilePickerRow
        showGradient
        onAdd={() => {
          saveScroll();
          saveItemCount(memories.length);
          navigate("/documents/health-memory/add");
        }}
        learnMoreContent={
          <>
            Some health experiences never make it into formal records--like an
            injury, a resolved condition, or a special need you or your loved
            ones recall.
            <br />
            <br />
            Capturing them as notes here adds missing context and builds a
            fuller health story to support better care.
          </>
        }
      />

      <MSpacer mt={32} />

      <div className="profile-flex-col-gap" style={{ marginTop: 0 }}>
        {!initialLoadDone || isLoading ? (
          <MLoader />
        ) : memories.length === 0 ? (
          <MEmptyCard
            primaryTitle="No health memory notes yet"
            icon={<IconBubble />}
            primaryCTA={{
              text: "Add health memory note ",
              onClick: () => {
                saveScroll();
                saveItemCount(memories.length);
                navigate("/documents/health-memory/add");
              },
            }}
          />
        ) : (
          <MInfiniteScroll
            data={memories}
            hasMore={hasMore}
            isLoading={isLoading}
            onLoadMore={fetchNextPage}
            renderItem={(memory) => {
              return (
                <HealthMemoryCard
                  key={memory.id}
                  id={memory.id}
                  title={memory.title}
                  year={memory.year}
                  month={memory.month}
                  hasVoiceNote={memory.hasVoiceNote ?? false}
                  voiceNoteDuration={memory.voiceNoteDuration}
                  playingId={playingId}
                  onPlay={(id) => setPlayingId(id)}
                  onFetchVoiceNoteUrl={() =>
                    getVoiceNoteUrl(memory.id).then((r) => r?.url ?? "")
                  }
                  transcript={memory.transcript}
                  transcriptStatus={memory.transcriptStatus}
                  transcriptEdited={memory.transcriptEdited}
                  onPatchTranscript={async (id, text) => {
                    await patchTranscript(id, text);
                    const data = await fetchHealthMemories({
                      page: 1,
                      pageSize: PAGE_SIZE,
                    });
                    setMemories(data || []);
                    setPage(2);
                    setHasMore((data || []).length === PAGE_SIZE);
                  }}
                  onRetryTranscript={async (id) => {
                    await retryTranscript(id);
                    const data = await fetchHealthMemories({
                      page: 1,
                      pageSize: PAGE_SIZE,
                    });
                    setMemories(data || []);
                    setPage(2);
                    setHasMore((data || []).length === PAGE_SIZE);
                  }}
                  optionsMenu={[
                    {
                      id: "edit",
                      label: "Edit",
                      icon: <IconEdit size={20} />,
                      onClick: () => {
                        saveScroll();
                        saveItemCount(memories.length);
                        navigate(
                          `/documents/health-memory/edit?mode=edit&id=${memory.id}`
                        );
                      },
                    },
                    {
                      id: "delete",
                      label: "Delete",
                      icon: <IconCircleX size={20} />,
                      onClick: () => {
                        setMemoryToDelete(memory);
                        setShowDeleteModal(true);

                        // Track delete click
                        trackUserDataLoaded({
                          memberInfo,
                          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
                          screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
                          action: "delete_click",
                        });
                      },
                      className: "delete",
                    },
                  ]}
                />
              );
            }}
          />
        )}
      </div>

      {showDeleteModal && memoryToDelete && (
        <MDialog
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setMemoryToDelete(null);
          }}
          title="Delete this memory?"
          description="This memory will be permanently removed from your health records."
          primaryCtaText="Yes"
          onPrimaryCta={async () => {
            if (!memoryToDelete) return;
            await deleteHealthMemory(memoryToDelete.id);

            // Track delete confirmation
            trackUserDataLoaded({
              memberInfo,
              screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
              screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
              action: "delete_confirm",
            });

            setShowDeleteModal(false);
            setMemoryToDelete(null);

            setIsLoading(true);
            const data = await fetchHealthMemories({
              page: 1,
              pageSize: PAGE_SIZE,
            });
            setMemories(data || []);
            setPage(2);
            setHasMore((data || []).length === PAGE_SIZE);
            setIsLoading(false);
          }}
          secondaryCtaText="No"
          onSecondaryCta={() => {
            setShowDeleteModal(false);
            setMemoryToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default HealthMemoryList;
